// RITA-1504 (Nutzungserfassung Phase 2b) — Formular-Vorgänge an der Maske (Entwurf C).
//
// Der Automat aus services/formUsage.ts kennt weder React noch Formik. Hier hängt er an einer
// echten Maske: <FormUsage> meldet Öffnen, Änderungsstand und Schließen für Masken mit
// <Formik>-Render-Prop, useFormUsageState dasselbe für Masken mit useFormik.
//
// Den AUSGANG des Absendens meldet immer die Maske selbst — er ist erst im .then/.catch der
// Mutation bzw. im Fehlerzweig der Speicherfunktion bekannt, und die liegen außerhalb des
// Formik-Baums. Dafür erzeugt die Maske ihren Automaten mit useFormUsageMachine und reicht ihn
// herein; ohne übergebenen Automaten legt sich der Haken einen eigenen an.
//
// Der Änderungsstand wird bei JEDER Darstellung nachgeführt, nie beim Schließen neu gelesen:
// wer vor dem Schließen zurücksetzt (Formik `resetForm`), soll trotzdem als Abbruch zählen.
import { useEffect, useMemo, useRef } from 'react';
import { useFormikContext } from 'formik';
import type { UsageFormName } from '../usageKeys';
import { createFormUsageMachine } from '../services/formUsage';
import type { FormUsageMachine } from '../services/formUsage';
import { usageTracker } from '../services/tracker';
import type { UsageRecord } from '../services/tracker';

/** Der Formik-Stand, soweit die Erfassung ihn braucht (Render-Prop und useFormik passen beide). */
export interface FormUsageSource {
  values: unknown;
  initialValues: unknown;
  dirty: boolean;
  isSubmitting: boolean;
}

export interface FormUsageOptions {
  /** Sichtbarkeit der Maske; bei Dialog-Formularen `open`. Die Flanke öffnet und schließt. */
  active?: boolean;
  /** Automat der Maske, wenn sie den Ausgang selbst meldet (useFormUsageMachine). */
  machine?: FormUsageMachine;
  /** Meldeweg; Tests setzen hier eine eigene Aufnahmestelle ein. */
  record?: UsageRecord;
}

const isRecordValue = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * Gleichheit zweier Feldwerte. Objekte und Listen werden über ihre JSON-Form verglichen, damit
 * ein unverändertes Unterobjekt nicht allein wegen einer neuen Objektidentität als Änderung
 * zählt. Ein nicht darstellbarer Wert (Zyklus) gilt als geändert — nie ein Fehler.
 */
const isSameFieldValue = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (isRecordValue(a) && isRecordValue(b)) {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * Anzahl der Top-Level-Schlüssel, deren Wert vom Ausgangswert abweicht (flacher Vergleich).
 * Es verlässt nur diese ZAHL die Maske, nie ein Feldwert.
 */
export const countChangedFields = (values: unknown, initialValues: unknown): number => {
  const current = isRecordValue(values) ? values : {};
  const initial = isRecordValue(initialValues) ? initialValues : {};
  const keys = Object.keys(initial);
  Object.keys(current).forEach((key) => {
    if (keys.indexOf(key) === -1) keys.push(key);
  });
  return keys.filter((key) => !isSameFieldValue(current[key], initial[key])).length;
};

/** Zuletzt verglichener Stand: zu welchen Objekt-Identitäten gehört welches Zählergebnis. */
interface ChangedFieldsCache {
  values: unknown;
  initialValues: unknown;
  changedFields: number;
}

export interface FormUsageMachineOptions {
  /** Meldeweg; Tests setzen hier eine eigene Aufnahmestelle ein. */
  record?: UsageRecord;
  /**
   * Kennung der Maskensitzung, z. B. die Datensatznummer aus der Route. Ändert sie sich, entsteht
   * ein neuer Automat: die alte Sitzung wird geschlossen (und wartet ggf. auf ihren späten
   * Ausgang), die neue geöffnet. Ohne Kennung lebt der Automat so lange wie die Komponente.
   */
  session?: string;
}

/**
 * Ein Automat je Maskensitzung, stabil über die Darstellungen hinweg. Die Maske meldet damit den
 * Ausgang des Absendens dort, wo sie ihn kennt, und reicht ihn an <FormUsage> weiter.
 * KI-Review !727 (2. Runde): auf parametrisierten Routen (/agent/:number) bleibt die Seite beim
 * Datensatzwechsel gemountet — deshalb bindet `session` den Automaten an den Datensatz.
 */
export const useFormUsageMachine = (name: UsageFormName, options: FormUsageMachineOptions = {}): FormUsageMachine => {
  const { record = usageTracker.record, session } = options;
  return useMemo(() => createFormUsageMachine(record, name, { session }), [name, record, session]);
};

/**
 * Meldet Öffnen, Änderungsstand und Schließen einer Maske. `form` ist der Formik-Stand
 * (useFormik-Rückgabe oder Render-Prop-Argument). Rückgabe ist der Automat, damit die Maske
 * den Ausgang melden kann.
 */
export const useFormUsageState = (
  name: UsageFormName,
  form: FormUsageSource | null | undefined,
  options: FormUsageOptions = {},
): FormUsageMachine => {
  const { active = true, machine, record } = options;
  const ownMachine = useFormUsageMachine(name, { record });
  const reporter = machine ?? ownMachine;
  const openRef = useRef(false);
  // KI-Review !727: Der Effekt unten läuft bewusst ohne Abhängigkeitsliste, sonst gingen die
  // Öffnen/Schließen-Flanken verloren. Der Feldvergleich darf deshalb aber nicht an jeder
  // Darstellung hängen — in großen Masken (berater.maske) läge er sonst im Tipp-Pfad. Formik gibt
  // bei jeder Feldänderung ein NEUES values-Objekt heraus, bei Darstellungen aus anderen Gründen
  // (Ladezustand, Panelwechsel, Elternteil) dasselbe. Wir merken uns daher die zuletzt
  // verglichenen Identitäten samt Ergebnis und zählen nur bei echt neuen Werten nach.
  const countRef = useRef<ChangedFieldsCache | null>(null);

  // Ohne Abhängigkeitsliste: der Änderungsstand gehört zu jeder Darstellung.
  useEffect(() => {
    if (!active) {
      if (openRef.current) {
        openRef.current = false;
        reporter.closed();
      }
      return;
    }
    if (!openRef.current) {
      openRef.current = true;
      reporter.opened();
    }
    let changedFields = 0;
    // Ohne dirty hat Formik die Gleichheit bereits festgestellt: nichts zu vergleichen.
    if (form && form.dirty) {
      const letzter = countRef.current;
      if (letzter && Object.is(letzter.values, form.values) && Object.is(letzter.initialValues, form.initialValues)) {
        changedFields = letzter.changedFields;
      } else {
        changedFields = countChangedFields(form.values, form.initialValues);
        countRef.current = { values: form.values, initialValues: form.initialValues, changedFields };
      }
    }
    reporter.update({
      changedFields,
      dirty: form ? form.dirty : false,
      submitting: form ? form.isSubmitting : false,
    });
  });

  // Abbau zählt wie Schließen: wer die Seite verlässt, hat die Maske verlassen.
  useEffect(
    () => () => {
      if (openRef.current) {
        openRef.current = false;
        reporter.closed();
      }
    },
    [reporter],
  );

  return reporter;
};

export interface FormUsageProps {
  /** Name der Maske aus usageKeys.ts (FORM_NAMES). */
  name: UsageFormName;
  /** Sichtbarkeit der Maske; bei Dialog-Formularen `open`. Vorgabe: sichtbar. */
  active?: boolean;
  /** Automat der Maske, wenn sie den Ausgang selbst meldet (useFormUsageMachine). */
  machine?: FormUsageMachine;
  /** Meldeweg; Tests setzen hier eine eigene Aufnahmestelle ein. */
  record?: UsageRecord;
}

/**
 * Baustein für Masken mit <Formik>-Render-Prop: gehört IN den Formik-Baum, zeichnet nichts und
 * ändert die Oberfläche nicht (kein Knoten im DOM).
 */
export function FormUsage({ name, active = true, machine, record }: FormUsageProps): null {
  const formik = useFormikContext<Record<string, unknown>>();
  useFormUsageState(name, formik, { active, machine, record });
  return null;
}

export default FormUsage;
